import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { embeddedViewToEditTransitionMetric, embeddedConfluenceIframeLoadMetrics } from './perf.config';
import { EMBEDDED_CONFLUENCE_METRICS_PREFIX, EMBEDDED_EDIT_CLICK, EMBEDDED_EDITOR_TTI, EMBEDDED_IFRAME_CONTENT_INITIALIZED, EMBEDDED_VIEW_TTI } from './constants';
import { fg } from '@atlaskit/platform-feature-flags';
// PerformanceMetricsProvider is only applied to internal EP
// Methods provided by PerformanceMetricsContext are defined by default as () => {}
// This allows instances not wrapped in provider (ie embedded-confluence public) to opperate without errors
var PerformanceMetricsContext = /*#__PURE__*/createContext({
  isInitialized: false,
  cancelIframeLoadMetrics: function cancelIframeLoadMetrics() {},
  markIframeLoadMetrics: function markIframeLoadMetrics() {},
  setContentType: function setContentType() {}
});

// calculate timestamp from iframe's performance metric post message
// relative to parent's window.performance.now() adjusted for post message latency
var adjustedPerformanceTimestamp = function adjustedPerformanceTimestamp(timestamp) {
  return typeof timestamp === 'number' ? window.performance.now() - (Date.now() - timestamp) : undefined;
};
var Provider = function Provider(_ref) {
  var children = _ref.children,
    contentType = _ref.contentType,
    sourceComponent = _ref.sourceComponent;
  var _useState = useState(contentType),
    _useState2 = _slicedToArray(_useState, 2),
    currentContentType = _useState2[0],
    setCurrentContentType = _useState2[1];
  var isInitialized = useRef(true);
  var iframeLoadMetrics = useRef();
  var embeddedViewToEditTransitionTimer = useRef();
  useLayoutEffect(function () {
    var _embeddedViewToEditTr;
    // TODO: Currently EP does not work with more that one instance per parent window
    // When this limitation is fixed we will also need to find a way to serialize post messages
    // this is because post message listeners will pick up on all messages coming to the parent window
    // triggering any callback listening to a given data.type message
    // therefore messages will need to be scoped to the particular instance they are intended for

    (_embeddedViewToEditTr = embeddedViewToEditTransitionTimer.current) === null || _embeddedViewToEditTr === void 0 || _embeddedViewToEditTr.cancel();
    embeddedViewToEditTransitionTimer.current = embeddedViewToEditTransitionMetric();

    // Setup iframe post message handling
    var handler = function handler(e) {
      var _iframeLoadMetrics$cu, _e$data$payload, _iframeLoadMetrics$cu2, _e$data$payload2, _embeddedViewToEditTr2, _e$data$payload3, _embeddedViewToEditTr3, _e$data$payload4, _iframeLoadMetrics$cu3, _e$data$payload5;
      switch (e.data.type) {
        case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_VIEW_TTI:
          (_iframeLoadMetrics$cu = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu === void 0 || _iframeLoadMetrics$cu.stop({
            stopTime: adjustedPerformanceTimestamp((_e$data$payload = e.data.payload) === null || _e$data$payload === void 0 ? void 0 : _e$data$payload.timestamp),
            customData: sourceComponent ? {
              sourceComponent: sourceComponent
            } : undefined
          });
          break;
        case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_EDITOR_TTI:
          (_iframeLoadMetrics$cu2 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu2 === void 0 || _iframeLoadMetrics$cu2.stop({
            stopTime: adjustedPerformanceTimestamp((_e$data$payload2 = e.data.payload) === null || _e$data$payload2 === void 0 ? void 0 : _e$data$payload2.timestamp),
            customData: sourceComponent ? {
              sourceComponent: sourceComponent
            } : undefined
          });
          (_embeddedViewToEditTr2 = embeddedViewToEditTransitionTimer.current) === null || _embeddedViewToEditTr2 === void 0 || _embeddedViewToEditTr2.stop({
            stopTime: adjustedPerformanceTimestamp((_e$data$payload3 = e.data.payload) === null || _e$data$payload3 === void 0 ? void 0 : _e$data$payload3.timestamp),
            customData: sourceComponent ? {
              sourceComponent: sourceComponent
            } : undefined
          });
          break;
        case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_EDIT_CLICK:
          (_embeddedViewToEditTr3 = embeddedViewToEditTransitionTimer.current) === null || _embeddedViewToEditTr3 === void 0 || _embeddedViewToEditTr3.start({
            startTime: adjustedPerformanceTimestamp((_e$data$payload4 = e.data.payload) === null || _e$data$payload4 === void 0 ? void 0 : _e$data$payload4.timestamp)
          });
          break;
        case EMBEDDED_CONFLUENCE_METRICS_PREFIX + EMBEDDED_IFRAME_CONTENT_INITIALIZED:
          (_iframeLoadMetrics$cu3 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu3 === void 0 || _iframeLoadMetrics$cu3.mark('iframe-content-initialized-end', adjustedPerformanceTimestamp((_e$data$payload5 = e.data.payload) === null || _e$data$payload5 === void 0 ? void 0 : _e$data$payload5.timestamp));
          break;
      }
    };
    window.addEventListener('message', handler);
    var currentTransitionTimer = embeddedViewToEditTransitionTimer.current;
    return function () {
      window.removeEventListener('message', handler);
      currentTransitionTimer.cancel();
    };
  }, [embeddedViewToEditTransitionTimer, sourceComponent]);

  // Set load metric based on content type
  useLayoutEffect(function () {
    if (currentContentType) {
      var _iframeLoadMetrics$cu4, _iframeLoadMetrics$cu5, _iframeLoadMetrics$cu6;
      // cancel current Embedded Confluence iframe load metric if necessary
      (_iframeLoadMetrics$cu4 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu4 === void 0 || _iframeLoadMetrics$cu4.cancel();
      iframeLoadMetrics.current = embeddedConfluenceIframeLoadMetrics(currentContentType);
      // start Embedded Confluence iframe load metric
      (_iframeLoadMetrics$cu5 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu5 === void 0 || _iframeLoadMetrics$cu5.start();
      (_iframeLoadMetrics$cu6 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu6 === void 0 || _iframeLoadMetrics$cu6.mark('lazy-load-start');
    }
    return function () {
      var _iframeLoadMetrics$cu7;
      (_iframeLoadMetrics$cu7 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu7 === void 0 || _iframeLoadMetrics$cu7.cancel();
    };
  }, [currentContentType, sourceComponent]);
  var cancelIframeLoadMetrics = useCallback(function () {
    var _iframeLoadMetrics$cu8;
    (_iframeLoadMetrics$cu8 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu8 === void 0 || _iframeLoadMetrics$cu8.cancel();
  }, []);
  var markIframeLoadMetrics = useCallback(function (markName) {
    var _iframeLoadMetrics$cu9;
    (_iframeLoadMetrics$cu9 = iframeLoadMetrics.current) === null || _iframeLoadMetrics$cu9 === void 0 || _iframeLoadMetrics$cu9.mark(markName);
  }, []);
  return /*#__PURE__*/React.createElement(PerformanceMetricsContext.Provider, {
    value: {
      cancelIframeLoadMetrics: cancelIframeLoadMetrics,
      markIframeLoadMetrics: markIframeLoadMetrics,
      isInitialized: isInitialized.current,
      setContentType: setCurrentContentType
    }
  }, children);
};
export var usePerformanceMetrics = function usePerformanceMetrics() {
  return useContext(PerformanceMetricsContext);
};
/**
 * Performance metrics provider wrapper that manages provider initialization in a hierarchical component tree.
 *
 * Key features:
 * - Ensures single initialization: Only the topmost provider in the component tree becomes active
 * - Handles content type updates: Child components can update metrics via setContentType
 * - Prevents duplicate metrics: Automatically skips initialization if a parent provider exists
 *
 * Example: When ViewPage is rendered within Page component, provider is only initialized by Page
 */
export var FeatureGatedPerformanceMetricsProvider = function FeatureGatedPerformanceMetricsProvider(_ref2) {
  var children = _ref2.children,
    contentType = _ref2.contentType,
    sourceComponent = _ref2.sourceComponent;
  var _usePerformanceMetric = usePerformanceMetrics(),
    isInitialized = _usePerformanceMetric.isInitialized,
    setContentType = _usePerformanceMetric.setContentType;
  useLayoutEffect(function () {
    // Update contentType when Page component replaces ViewPage with EditPage or vice versa
    isInitialized && contentType && setContentType(contentType);
  }, [isInitialized, contentType, setContentType]);
  return !isInitialized ? /*#__PURE__*/React.createElement(Provider, {
    contentType: contentType,
    sourceComponent: sourceComponent
  }, children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
};
export var PerformanceMetricsProvider = function PerformanceMetricsProvider(_ref3) {
  var children = _ref3.children,
    contentType = _ref3.contentType,
    sourceComponent = _ref3.sourceComponent;
  return fg('embedded-confluence-performance-markers') ? /*#__PURE__*/React.createElement(FeatureGatedPerformanceMetricsProvider, {
    contentType: contentType,
    sourceComponent: sourceComponent
  }, children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
};